/**
 * 表示の広告（Adsterra の通常バナー・Social Bar）のカードの判断（2026-10-19 creator/ads-open・components/DisplayAdsCard.tsx が使う）。
 *
 * WHY 機能の一覧（FeaturesCard）の行にしないで専用のカードにしたか:
 *   機能の一覧はサーバーの GET …/features を CLI・MCP と共有している。そこに行を足すと、審査中で変えない決まりの CLI・MCP の一覧にも出る
 *   （docs/teams/creator/tasks/adsterra-creators.md「やらないこと」）。また、ON にしたときに収益の決まり（100%・表示の回数の比の概算・
 *   Adsterra の確定と 60 日の確認期間）を正直に見せる場所が要る。そこでダッシュボードだけのカードにし、反映は設定のページと同じ
 *   PUT …/config（サーバーの検証・監査ログをそのまま使う）にした。
 * サーバー側の差し込み・数え・配分は apps/server/src/modules/ads/displayAds.ts。
 *
 * このファイルは純粋な関数だけ（featureToggle.ts と同じ理由: `node --test` で型の除去だけで動かす）。import は拡張子つき
 */
import { isLaunchHidden } from './launchHidden.ts';

export type DisplayAdKind = 'banner' | 'socialBar';

/** カードの行（並び順どおり）。configPath は手元の kumo.config.json に同じ変更を入れる案内に出す */
export const DISPLAY_AD_KINDS: ReadonlyArray<{ kind: DisplayAdKind; configPath: string }> = [
  { kind: 'banner', configPath: 'ads.banner.enabled' },
  { kind: 'socialBar', configPath: 'ads.socialBar.enabled' }
];

type Cfg = { features?: { hosting?: boolean }; ads?: { banner?: { enabled?: boolean }; socialBar?: { enabled?: boolean } } };

/** サーバーが返した正規化済みの config から今の状態（hosting = KUMODeck の配信。OFF なら広告を入れる先が無い） */
export function displayAdsState(config: unknown): { banner: boolean; socialBar: boolean; hosting: boolean } {
  const c = (config ?? {}) as Cfg;
  return { banner: c.ads?.banner?.enabled === true, socialBar: c.ads?.socialBar?.enabled === true, hosting: c.features?.hosting === true };
}

/** config に 1 つのスイッチだけを当てた新しいオブジェクト（元は変えない。リワード動画の ads.enabled など他は触らない） */
export function withDisplayAd<T>(config: T, kind: DisplayAdKind, on: boolean): T {
  const copy = JSON.parse(JSON.stringify(config ?? {})) as Record<string, unknown>;
  const ads = (copy.ads && typeof copy.ads === 'object' ? copy.ads : (copy.ads = {})) as Record<string, unknown>;
  ads[kind] = { ...((ads[kind] as object | undefined) ?? {}), enabled: on };
  return copy as T;
}

/** カードを出すか。広告（ads）を最初の公開で隠している間は出さない（閉じ直すときは launchHidden.ts に 'ads' を戻すだけ） */
export function displayAdsShown(): boolean {
  return !isLaunchHidden('ads');
}

/**
 * Social Bar を選ぶ前に見せる事実の文の鍵（並び順どおり。2026-10-19 founder「ちゃんとどういう広告なのかは説明する」・tasks/social-bar-explain.md）。
 * カードの「どんな広告か」と、ON にする確認のダイアログの両方に同じものを出す（カードを開かずに押しても、確認で必ず読める）。
 * 事実の元: examples/adsterra-banner-demo/README.md（画面の上に重なる・形は Adsterra が選ぶ・タブの文字を変える・報酬の受け取りは無い）
 */
export const SOCIAL_BAR_FACTS = ['over', 'forms', 'title', 'noReward'] as const;

/**
 * カードの説明をどの決まりで出すか（2026-10-27 console/earn-ads-card・指示書 docs/teams/console/tasks/earn-ads-card.md）。
 *
 * WHY: 「初日から稼げる」（Sara の試し・11/2 開始）の作り手は、このカードで帯を ON にする（告知のメール「switch on "KUMODeck ads" on its Overview」）。
 *   前のカードは Adsterra の時の説明（売上の 100%・表示の比の概算・Adsterra の確定と 60 日）だけ = 試しの作り手に違う話が見えていた。
 *   サーバーは作り手ごとに出す広告が違う（apps/server/src/modules/ads/houseScope.ts）ので、カードもサーバーの口で分ける:
 *     earn     … GET /v1/money/earn が 200（試しに入っている = KUMODeck 自身の広告・1 プレイの額・週の入れ物・ギフトカード）
 *     house    … earn は 404 だが GET /v1/money/self-ads/house-hosts/:id が 200（旗 house の範囲の中 = KUMODeck 自身の広告だけが出る・試しは動いていない）
 *     external … 両方 404 = サーバーはこのゲームを旗 off と同じに扱う（範囲 internal の外 = 今の本番では Adsterra の帯）→ 前の Adsterra の説明のまま
 *       （指示書は「入っていない作り手には house の決まり」だが、範囲の外のゲームに house の帯は出ない = house の文はお金の約束が嘘になる。
 *        Adsterra の文を消すのは KUMO_HOUSE_ADS_SCOPE=all の日（founder の別の OK）に、ここを 'house' に寄せるだけ。最後の報告に書いた）
 *     loading  … どちらかがまだ分からない = お金の決まりの行を出さない（違う決まりが一瞬見えるより、出ないほうがよい）
 * 404 以外の失敗（5xx・回線）も「入っていない」と読む: 外れると古い Adsterra の文が出るだけで、操作（ON / OFF）は止めない。
 */
export type AdsCardMode = 'loading' | 'earn' | 'house' | 'external';
export type Signal = 'yes' | 'no' | 'unknown';

/** useResource の結果を 3 つに（データあり = yes・失敗 = no・読み込み中 = unknown） */
export function signalOf(r: { data: unknown; error: unknown }): Signal {
  if (r.data !== null && r.data !== undefined) return 'yes';
  return r.error ? 'no' : 'unknown';
}

export function adsCardMode(earn: Signal, house: Signal): AdsCardMode {
  if (earn === 'yes') return 'earn';
  if (earn === 'unknown') return 'loading';
  if (house === 'yes') return 'house';
  return house === 'no' ? 'external' : 'loading';
}

/** KUMODeck 自身の広告だけが出る決まりか（earn / house）。このときバナーの枠だけが使われる（Social Bar は置かない = サーバーの selfAdsServe.ts houseBand） */
export const kumoAdsOnly = (mode: AdsCardMode): boolean => mode === 'earn' || mode === 'house';

/**
 * 出す行。KUMODeck 自身の広告だけの間は Social Bar の行を出さない（ON にしても何も出ない・説明が Adsterra の物）。
 * ただし既に ON の Social Bar は行を残して OFF にできるようにする（範囲の外に戻った日に Adsterra の Social Bar が急に出ないように）
 */
export function shownKinds(mode: AdsCardMode, state: { socialBar: boolean }): DisplayAdKind[] {
  return DISPLAY_AD_KINDS.map((k) => k.kind).filter((k) => k === 'banner' || !kumoAdsOnly(mode) || state.socialBar);
}
